import { useNavigate } from 'react-router-dom';
import { resolveChatbotConversationPath } from '../../../features/chatbots/chatbotTabs';
import type { useChatbotConversationsTab } from '../../../features/chatbots/hooks/useChatbotConversationsTab';
import { useI18n } from '../../../i18n/useI18n';
import { formatTimestamp } from '../../../utils/formatUtils';
import './ChatbotConversationsTab.css';

type ChatbotConversationsTabProps = {
  chatbotId: string;
  conversationsTab: ReturnType<typeof useChatbotConversationsTab>;
};

function formatConversationTime(
  value: string | null | undefined,
  locale: string,
  fallback: string,
): string {
  if (!value) {
    return fallback;
  }
  return formatTimestamp(value, locale);
}

export function ChatbotConversationsTab({
  chatbotId,
  conversationsTab,
}: ChatbotConversationsTabProps) {
  const { locale, translate } = useI18n();
  const navigate = useNavigate();
  const notAvailable = translate('chatbots.notAvailable');
  const { conversations, conversationsQuery, isPageLoading } = conversationsTab;

  if (isPageLoading) {
    return (
      <p className="chatbot-conversations__status">
        {translate('chatbots.conversations.loading')}
      </p>
    );
  }

  if (conversationsQuery.isError) {
    return (
      <p className="chatbot-conversations__status" role="alert">
        {translate('chatbots.conversations.loadError')}
      </p>
    );
  }

  if (conversations.length === 0) {
    return (
      <p className="chatbot-conversations__status">
        {translate('chatbots.conversations.empty')}
      </p>
    );
  }

  return (
    <section className="chatbot-conversations">
      <ul className="chatbot-conversations__list">
        {conversations.map((conversation) => {
          const latestUserText =
            conversation.lastUserMessage?.content?.trim() ?? '';

          return (
            <li key={conversation.id}>
              <button
                type="button"
                className="chatbot-conversations__card"
                onClick={() =>
                  navigate(
                    resolveChatbotConversationPath(chatbotId, conversation.id),
                  )
                }
              >
                <dl className="chatbot-conversations__card-meta">
                  <div className="chatbot-conversations__card-stat">
                    <dt>
                      {translate('chatbots.conversations.columns.started')}
                    </dt>
                    <dd>
                      {formatConversationTime(
                        conversation.startedAt ?? conversation.created,
                        locale,
                        notAvailable,
                      )}
                    </dd>
                  </div>
                  <div className="chatbot-conversations__card-stat">
                    <dt>
                      {translate('chatbots.conversations.columns.lastMessage')}
                    </dt>
                    <dd>
                      {formatConversationTime(
                        conversation.lastMessageAt,
                        locale,
                        notAvailable,
                      )}
                    </dd>
                  </div>
                  <div className="chatbot-conversations__card-stat">
                    <dt>
                      {translate('chatbots.conversations.columns.messages')}
                    </dt>
                    <dd>{conversation.messageCount ?? 0}</dd>
                  </div>
                </dl>
                <div className="chatbot-conversations__card-preview">
                  <span className="chatbot-conversations__card-preview-label">
                    {translate('chatbots.conversations.lastUserMessage')}
                  </span>
                  <p
                    className={[
                      'chatbot-conversations__card-preview-text',
                      latestUserText === '' &&
                        'chatbot-conversations__card-preview-text--empty',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {latestUserText !== ''
                      ? latestUserText
                      : translate(
                          'chatbots.conversations.lastUserMessageEmpty',
                        )}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
